import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "../../components/shared/PageHeader";
import {
  usePlatformEnterprises, usePlatformUsers, useAllFeedback, useTopUsedTasks,
} from "@platform/hooks";
import { Building2, Users, CheckSquare, MessageSquare, Search } from "lucide-react";

interface Enterprise { id: string; name: string; slug: string; status: "active" | "suspended" | "pending"; createdAt?: string }
interface PlatformUserRow { id: string; createdAt?: string }
interface FeedbackRow { id: string; status: "open" | "under_review" | "resolved" | "closed" }
interface TopTaskRow { taskId: string; taskName: string; moduleName: string; instanceCount: number; lastUsed: string | null }

const MODULE_COLORS: Record<string, string> = {
  Payroll: "bg-blue-50 text-blue-700 border-blue-100",
  Absence: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Benefits: "bg-purple-50 text-purple-700 border-purple-100",
};
function moduleColor(name: string) {
  return MODULE_COLORS[name] ?? "bg-gray-50 text-gray-600 border-gray-200";
}

function formatRelativeTime(iso: string | null) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ── Insight card — icon badge + value + a real delta computed from actual
//    data. No sparkline: a 7-day trend line needs a day-by-day time series
//    this page doesn't have without a new endpoint, and a fabricated trend
//    would be actively misleading rather than just decorative. ────────────
function InsightCard({
  label, value, delta, iconBg, iconColor, icon: Icon, onClick,
}: {
  label: string; value: number; delta: string;
  iconBg: string; iconColor: string; icon: React.ComponentType<{ className?: string }>;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-[12px] border p-4 transition-colors ${
        onClick ? "cursor-pointer border-ds-border hover:border-indigo-300" : "border-ds-border"
      }`}
    >
      <div className="flex items-start justify-between mb-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-ds-text-muted">{label}</p>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
      </div>
      <p className="text-[28px] font-semibold text-ds-text leading-none mt-1 mb-3">{value}</p>
      <p className="text-xs text-ds-text-secondary pt-2.5 border-t border-ds-border">{delta}</p>
    </div>
  );
}

const WINDOW_OPTIONS = [
  { key: "all", label: "All time", days: undefined },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
] as const;

// Evaluated once at module load — Date.now() called directly during
// render trips the react-hooks/purity rule (component render must be
// idempotent; a live clock read isn't). Same pattern ConfigIQ's own
// ported dashboard already uses for identical delta computations.
const RENDER_TIME = Date.now();

export function PlatformDashboardPage() {
  const navigate = useNavigate();
  const { data: enterprises = [] } = usePlatformEnterprises();
  const { data: users = [] } = usePlatformUsers();
  const { data: feedback = [] } = useAllFeedback();

  const [windowKey, setWindowKey] = useState<(typeof WINDOW_OPTIONS)[number]["key"]>("all");
  const selectedWindow = WINDOW_OPTIONS.find((w) => w.key === windowKey)!;
  const { data: topTasks = [], isLoading: topTasksLoading } = useTopUsedTasks(selectedWindow.days);
  const { data: todayTasks = [] } = useTopUsedTasks(1);
  const [taskSearch, setTaskSearch] = useState("");

  const typedEnterprises = enterprises as Enterprise[];
  const typedUsers = users as PlatformUserRow[];
  const typedFeedback = feedback as FeedbackRow[];
  const typedTopTasks = topTasks as TopTaskRow[];
  const typedTodayTasks = todayTasks as TopTaskRow[];

  const DAY = 86400000;
  const now = RENDER_TIME;

  const enterprisesThisMonth = typedEnterprises.filter(
    (e) => e.createdAt && now - new Date(e.createdAt).getTime() < 30 * DAY,
  ).length;
  const usersThisWeek = typedUsers.filter(
    (u) => u.createdAt && now - new Date(u.createdAt).getTime() < 7 * DAY,
  ).length;
  const totalTaskInstances = typedTopTasks.reduce((sum, t) => sum + t.instanceCount, 0);
  const taskInstancesToday = typedTodayTasks.reduce((sum, t) => sum + t.instanceCount, 0);
  const openFeedback = typedFeedback.filter((f) => f.status === "open" || f.status === "under_review").length;

  const maxInstanceCount = Math.max(...typedTopTasks.map((t) => t.instanceCount), 1);
  const filteredTasks = useMemo(
    () => typedTopTasks.filter((t) => t.taskName.toLowerCase().includes(taskSearch.toLowerCase())),
    [typedTopTasks, taskSearch],
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Platform overview" subtitle="All enterprises, users, and task activity at a glance." />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <InsightCard
          label="Enterprises" value={typedEnterprises.length}
          delta={enterprisesThisMonth > 0 ? `+${enterprisesThisMonth} this month` : "No new enterprises this month"}
          iconBg="bg-purple-50" iconColor="text-purple-600" icon={Building2}
          onClick={() => navigate("/platform/enterprises")}
        />
        <InsightCard
          label="Total users" value={typedUsers.length}
          delta={usersThisWeek > 0 ? `+${usersThisWeek} this week` : "No new users this week"}
          iconBg="bg-orange-50" iconColor="text-orange-600" icon={Users}
          onClick={() => navigate("/platform/users")}
        />
        <InsightCard
          label="Total tasks" value={totalTaskInstances}
          delta={taskInstancesToday > 0 ? `+${taskInstancesToday} new today` : "No new tasks today"}
          iconBg="bg-blue-50" iconColor="text-blue-600" icon={CheckSquare}
        />
        <InsightCard
          label="Feedback" value={typedFeedback.length}
          delta={openFeedback > 0 ? `${openFeedback} open` : "None open"}
          iconBg="bg-pink-50" iconColor="text-pink-600" icon={MessageSquare}
          onClick={() => navigate("/platform/feedback")}
        />
      </div>

      <div className="bg-white rounded-[12px] border border-ds-border overflow-hidden">
        <div className="px-5 py-4 border-b border-ds-border">
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-ds-text">Top used tasks</h2>
            <p className="text-xs text-ds-text-muted mt-0.5">Every instance, any status, any user</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-ds-bg rounded-lg p-0.5 gap-0.5">
              {WINDOW_OPTIONS.map((w) => (
                <button
                  key={w.key}
                  onClick={() => setWindowKey(w.key)}
                  className={`px-3 h-7 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                    windowKey === w.key ? "bg-white text-ds-text shadow-sm font-semibold" : "text-ds-text-secondary hover:text-ds-text"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ds-text-muted pointer-events-none" />
              <input
                type="text" value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)}
                placeholder="Search task name…"
                className="h-8 w-48 rounded-lg border border-ds-border bg-ds-bg pl-8 pr-3 text-xs text-ds-text placeholder:text-ds-text-muted outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10"
              />
            </div>
          </div>
        </div>

        {topTasksLoading && <div className="px-5 py-8 text-center text-sm text-ds-text-muted">Loading…</div>}

        {!topTasksLoading && filteredTasks.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-ds-text-muted">
            {taskSearch ? "No tasks match your search." : "No tasks started in this window."}
          </div>
        )}

        {!topTasksLoading && filteredTasks.map((t, i) => (
          <div key={t.taskId} className="flex items-center gap-3 px-5 py-3.5 border-b border-ds-border last:border-0">
            <span className="text-xs text-ds-text-muted w-4">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-ds-text">{t.taskName}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-md border ${moduleColor(t.moduleName)}`}>{t.moduleName}</span>
              </div>
              <div className="h-[5px] bg-ds-bg rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500" style={{ width: `${(t.instanceCount / maxInstanceCount) * 100}%` }} />
              </div>
            </div>
            <span className="text-sm font-medium text-ds-text w-24 text-right shrink-0">{t.instanceCount} instances</span>
            <span className="text-xs text-ds-text-muted w-16 text-right shrink-0">{formatRelativeTime(t.lastUsed)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
